import type { CSSProperties } from 'react';
import { Toaster } from 'sonner';

import messages from '@/shared/i18n/en/shell';
import { useT } from '@/shared/i18n/react';
import { useThemeStore } from '@/shared/stores/theme.store';

/**
 * Sonner toaster wired to the app's chosen theme.
 *
 * The app themes via the `data-theme` attribute driven by the theme store,
 * so a hardcoded `theme="system"` on the Toaster would render light toasts on
 * a dark app (or vice-versa) whenever the user's explicit choice differs from
 * their OS. Mirroring the store value keeps toasts in lockstep with the UI —
 * the store's 'light' | 'dark' | 'system' union matches sonner's `theme` prop.
 */
export function ThemedToaster() {
  const theme = useThemeStore((s) => s.theme);
  const t = useT(messages);
  return (
    <Toaster
      // top-center so the toast never collides with the mobile bottom-nav
      // (5-slot bar pinned to the viewport bottom). Desktop is fine with it too.
      position="top-center"
      theme={theme}
      richColors
      closeButton
      containerAriaLabel={t('notifications')}
      // Toasts are a raised surface like the sheets: the app's elevated
      // background, its border, 16 px corners and the app font (sonner
      // otherwise paints its own greys and system font).
      style={TOAST_SURFACE}
      toastOptions={{
        // Breathing room from the status bar on installed PWAs / notches.
        style: { marginTop: 'env(safe-area-inset-top, 0px)', fontFamily: 'var(--font-sans)' },
      }}
    />
  );
}

/** sonner's theme hooks, pointed at the app tokens (light + dark follow `data-theme`). */
const TOAST_SURFACE = {
  '--normal-bg': 'var(--bg-elevated)',
  '--normal-border': 'var(--border)',
  '--normal-text': 'var(--fg)',
  '--border-radius': '16px',
} as CSSProperties;
